// ConfirmDialog.jsx — replaces window.confirm() for destructive actions.
//
// Controlled modal: pass `open`, `onConfirm`, `onCancel`. Intentionally
// lightweight — no portal; renders inside the calling tree, but uses a
// fixed-position overlay so it still covers the viewport. Keyboard:
// Escape cancels, Enter confirms. Focus is trapped to the action buttons.

import React, { useEffect, useRef } from 'react';
import RetroButton from './RetroButton.jsx';

export default function ConfirmDialog({
  open,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger', // "danger" | "primary"
  onConfirm,
  onCancel,
}) {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    // Focus the primary action when the dialog opens.
    confirmRef.current?.focus();

    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel?.();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm?.();
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onConfirm, onCancel]);

  if (!open) return null;

  return (
    <div
      className="retro-dialog-overlay"
      onClick={onCancel}
      role="presentation"
    >
      <div
        className="win95-window"
        style={{ maxWidth: 460, width: '100%' }}
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="retro-dialog-title"
        aria-describedby="retro-dialog-body"
      >
        <div className="win95-titlebar" style={{ cursor: 'default' }}>
          <span className="win95-titlebar__label" id="retro-dialog-title">{title}</span>
          <div className="win95-titlebar__btns">
            <button
              type="button"
              className="win95-close-btn"
              aria-label={cancelLabel}
              onClick={onCancel}
            >
              ✕
            </button>
          </div>
        </div>
        <div className="retro-dialog-body" id="retro-dialog-body">
          {typeof message === 'string' ? <p>{message}</p> : message}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
            padding: '4px 14px 14px',
          }}
        >
          <RetroButton onClick={onCancel}>{cancelLabel}</RetroButton>
          <RetroButton
            variant={variant}
            onClick={onConfirm}
            ref={confirmRef}
            className="win95-btn--default"
          >
            {confirmLabel}
          </RetroButton>
        </div>
      </div>
    </div>
  );
}
